import { useEffect, useState } from 'react'

import { UIBottomSheet, UIButton, UIFlex, UIGridItem, UISpacing, UIText } from 'components'
import { useGlobalContext } from 'contexts/global'
import { getConsents, postRevokeConsent } from 'services/consents'
import { ErrorBottomsheet } from 'components/ErrorBottomsheet'
import { INTER_ID_FLUXO, PRODUCT, SCREEN_NAMES } from 'constants/analytics'
import { Feedback } from 'components/Feedback'
import { TConsent, TConsentsResponse } from 'services/consents/types'
import { SkeletoIntro } from 'pages/Login/Intro/skeleton'

import { useNotification } from '@interco/inter-ui/components/Notification'
import { formatToBrazilianDate } from '@interco/lib-util'
import { interWbSession } from '@interco/inter-webview-bridge'

import { BulletList, BulletPoint, ConnectionsCard, HeaderInfosGrid } from './styles'

export const Connections = () => {
  const { setHeaderPage } = useGlobalContext()

  const [selectedService, setSelectedService] = useState({
    name: '',
    id: '',
  })
  const [bottomsheet, setBottomsheet] = useState<boolean>(false)
  const [consentsList, setConsentsList] = useState<TConsentsResponse[]>([])
  const [loadingRevokeConsent, setLoadingRevokeConsent] = useState<boolean>(false)
  const [showErrorBottomsheet, setShowErrorBottomsheet] = useState<boolean>(false)
  const [loadingGlobal, setLoadingGlobal] = useState<boolean>(false)

  const { showNotification } = useNotification()

  const urlParams = new URLSearchParams(window.location.search)

  useEffect(() => {
    setHeaderPage({ title: 'Conexões' })
  }, [])

  const getConsentsService = async () => {
    setLoadingGlobal(true)
    getConsents(urlParams.get('authorization_id') || '')
      .then((data) => {
        setConsentsList(data)
      })
      .catch(() => {
        setShowErrorBottomsheet(true)
      })
      .finally(() => setLoadingGlobal(false))
  }

  useEffect(() => {
    getConsentsService()
  }, [])

  const handleRevokeConsent = (clientId: string) => {
    setBottomsheet(true)
    setLoadingRevokeConsent(true)
    postRevokeConsent({
      clientId,
      authorizationId: urlParams.get('authorization_id') || '',
    })
      .then(() => {
        interWbSession.requestAnalytics(INTER_ID_FLUXO, {
          screen: `${PRODUCT}${SCREEN_NAMES.CONNECTIONS}`,
          content_action: 'Toque',
          action_id: 'Cancelar conexão',
          adjust_event_token: '0',
          conexao: clientId,
        })

        getConsentsService()

        showNotification({
          title: `Conexão com ${clientId} cancelada!`,
          content: '',
          type: 'SUCCESS',
        })
      })
      .catch(() => {
        showNotification({
          title: `Não foi possível cancelar a conexão com ${clientId}`,
          content: 'Tente novamente mais tarde.',
          type: 'ERROR',
        })
      })
      .finally(() => {
        setLoadingRevokeConsent(false)
        setBottomsheet(false)
      })
  }

  const bulletList = (list: string[]) =>
    list.map((item) => (
      <div className="bullet-list" style={{ margin: '0px 0px 0px 8px' }}>
        <BulletList>
          <BulletPoint key={item} />
          <UIText variant="caption-1" a="span" color="var(--typography500)">
            {item}
          </UIText>
        </BulletList>
      </div>
    ))

  const containsActiveConsents = consentsList.some((product) =>
    product.consents.some((item) => item.active === true),
  )

  const getLastRevokedConsentDate = (revokedList: TConsent[]): string | null => {
    if (!revokedList.some((item) => item.revokedAt !== null)) return null

    const sorted = revokedList
      .filter((item) => item.revokedAt !== null && item.revokedAt !== undefined)
      .sort((a, b) => new Date(b.revokedAt!).getTime() - new Date(a.revokedAt!).getTime())

    return formatToBrazilianDate(new Date(sorted[0].revokedAt!))
  }

  return consentsList && !loadingGlobal ? (
    <div>
      {consentsList.length !== 0 ? (
        <div className="connections-container">
          {containsActiveConsents && (
            <div className="active-connections-title">
              <UIText variant="body-1" as="p" color="var(--typography500)" bold>
                Ativas
              </UIText>
              <UISpacing mb="xs" />
            </div>
          )}

          {consentsList.map((product) => {
            const activeConsents = product.consents.filter((item) => item.active === true)

            if (activeConsents) {
              return activeConsents.map((activeConsent) => (
                <ConnectionsCard className="active-connections-card" key={`${product.id}-active`}>
                  <HeaderInfosGrid className="header-active-infos-card-grid">
                    <UIGridItem row="span 2">
                      <img src={product.logo} alt={product.name} width={64} height={64} />
                    </UIGridItem>
                    <UIGridItem row="span 2">
                      <UIText variant="body-3" as="p" color="var(--typography500)" bold>
                        {product.name}
                      </UIText>
                      <UIText variant="caption-1" as="p" color="var(--typography500)" bold>
                        Conectada em{' '}
                        {activeConsent.consentGivenAt &&
                          formatToBrazilianDate(new Date(activeConsent.consentGivenAt))}
                      </UIText>
                    </UIGridItem>
                  </HeaderInfosGrid>

                  <UISpacing mb="xs" />

                  <div className="consents-data-container">
                    {activeConsent.claims.length > 0 && (
                      <div className="shared-data-title">
                        <UIText variant="caption-1" as="p" color="var(--typography500)" bold>
                          Dados compartilhados
                        </UIText>
                        <UISpacing mb="xxxs" />
                      </div>
                    )}

                    {activeConsent.claims && bulletList(activeConsent.claims)}

                    {activeConsent.scope.length > 0 && (
                      <div className="authorized-actions-title">
                        <UISpacing mb="xs" />
                        <UIText variant="caption-1" as="p" color="var(--typography500)" bold>
                          Ações autorizadas
                        </UIText>
                        <UISpacing mb="xxxs" />
                      </div>
                    )}

                    {activeConsent.scope && bulletList(activeConsent.scope)}
                  </div>

                  <UISpacing mb="sm" />

                  <UIButton
                    variant="secondary"
                    size="small"
                    fullWidth
                    onClick={() => {
                      setSelectedService({
                        name: product.name,
                        id: product.id,
                      })
                      setBottomsheet(true)
                    }}
                  >
                    Cancelar conexão
                  </UIButton>
                </ConnectionsCard>
              ))
            }
            return null
          })}

          {containsActiveConsents && <UISpacing mb="md" />}

          {consentsList.some((product) =>
            product.consents.every((item) => item.active === false),
          ) && (
            <div className="canceled-connections-title">
              <UIText variant="body-1" as="p" color="var(--typography500)" bold>
                Canceladas
              </UIText>
              <UISpacing mb="xs" />
            </div>
          )}

          {consentsList.map((product) => {
            if (product.consents.every((item) => item.active === false)) {
              return (
                <ConnectionsCard
                  className="canceled-connections-card"
                  key={`${product.id}-inactive`}
                >
                  <HeaderInfosGrid className="header-canceled-infos-card-grid">
                    <UIGridItem row="span 2">
                      <img src={product.logo} alt={product.name} width={64} height={64} />
                    </UIGridItem>
                    <UIGridItem row="span 2">
                      <UIText variant="body-3" as="p" color="var(--typography500)" bold>
                        {product.name}
                      </UIText>
                      {product.consents.some((item) => item.active === false) && (
                        <UIText variant="caption-1" as="p" color="var(--typography500)" bold>
                          Cancelada em {getLastRevokedConsentDate(product.consents)}
                        </UIText>
                      )}
                    </UIGridItem>
                  </HeaderInfosGrid>
                </ConnectionsCard>
              )
            }
            return null
          })}

          {bottomsheet && (
            <UIBottomSheet onClose={() => setBottomsheet(false)}>
              <UIFlex direction="column" alignItems="normal">
                <UIText variant="headline-h3" semiBold>
                  Deseja excluir a conexão com {selectedService.name}?
                </UIText>
                <UISpacing mb="xs" />
                <UIText variant="body-3">
                  Seus dados não serão mais compartilhados com {selectedService.name} e as ações
                  autorizadas não estarão mais disponíveis.
                </UIText>
                <UISpacing mb="xxs" />
                <UIText variant="body-3">
                  Para se conectar novamente, você precisará realizar uma nova autenticação.
                </UIText>
                <UISpacing mb="sm" />
                <UIButton
                  onClick={() => handleRevokeConsent(selectedService.id)}
                  isLoading={loadingRevokeConsent}
                >
                  Cancelar conexão
                </UIButton>
                <UISpacing mb="xxs" />
                <UIButton
                  variant="secondary"
                  onClick={() => setBottomsheet(false)}
                  disabled={loadingRevokeConsent}
                >
                  Voltar
                </UIButton>
              </UIFlex>
            </UIBottomSheet>
          )}

          {showErrorBottomsheet && (
            <ErrorBottomsheet
              title="Ocorreu um erro"
              description="Não foi possível buscar as conexões. Tente novamente mais tarde."
              onClose={() => setShowErrorBottomsheet(false)}
            />
          )}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '100px',
          }}
        >
          <Feedback status="noConsents" />
        </div>
      )}
    </div>
  ) : (
    <div>{loadingGlobal && <SkeletoIntro />}</div>
  )
}
